import React, { useRef, useState } from 'react';
import { Upload, Trash2, GripVertical, FileText, Image as ImageIcon, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MediaItemsField — admin editor for the media_items custom field type.
 * Each item = { id, url, filename, caption, type: 'image'|'pdf' }
 * Uploads to /api/media/upload (Emergent Object Storage).
 */
export default function MediaItemsField({ value = [], onChange }) {
  const items = Array.isArray(value) ? value : [];
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await fetch(`${API}/api/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (r.ok && data.url) {
        const isPdf = (file.type === 'application/pdf') || file.name.toLowerCase().endsWith('.pdf');
        const newItem = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          url: data.url,
          filename: file.name,
          caption: file.name.replace(/\.[^.]+$/, ''),
          type: isPdf ? 'pdf' : 'image',
        };
        onChange([...items, newItem]);
        toast.success(`Uploaded ${file.name}`);
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const updateItem = (id, patch) => {
    onChange(items.map(i => i.id === id ? { ...i, ...patch } : i));
  };
  const removeItem = (id) => {
    onChange(items.filter(i => i.id !== id));
  };
  const moveItem = (id, dir) => {
    const idx = items.findIndex(i => i.id === id);
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= items.length) return;
    const copy = [...items];
    [copy[idx], copy[swap]] = [copy[swap], copy[idx]];
    onChange(copy);
  };

  return (
    <div className="space-y-3" data-testid="media-items-field">
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-xs text-gray-500 border border-dashed rounded-lg p-4 text-center">
            No media items yet. Upload images or PDFs below.
          </div>
        )}
        {items.map((item, idx) => {
          const absUrl = item.url?.startsWith('http') ? item.url : `${API}${item.url}`;
          const isPdf = item.type === 'pdf';
          return (
            <div key={item.id} className="flex items-start gap-3 border rounded-lg p-2 bg-white" data-testid={`media-item-${idx}`}>
              <div className="flex flex-col items-center gap-0.5 pt-1">
                <GripVertical className="w-4 h-4 text-gray-300" />
                <button onClick={() => moveItem(item.id, 'up')} disabled={idx === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
                <button onClick={() => moveItem(item.id, 'down')} disabled={idx === items.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
              </div>
              <div className="w-20 h-20 rounded overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                {isPdf ? (
                  <div className="flex flex-col items-center text-red-600"><FileText className="w-6 h-6" /><span className="text-[9px] font-bold">PDF</span></div>
                ) : (
                  <img src={absUrl} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <Input
                  value={item.caption || ''}
                  onChange={e => updateItem(item.id, { caption: e.target.value })}
                  placeholder="Caption / label"
                  className="h-8 text-sm"
                  data-testid={`media-caption-${idx}`}
                />
                <div className="text-[11px] text-gray-500 truncate flex items-center gap-1">
                  {isPdf ? <FileText className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                  {item.filename || item.url}
                </div>
              </div>
              <button onClick={() => removeItem(item.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`media-remove-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <div>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={e => upload(e.target.files?.[0])} className="hidden" data-testid="media-file-input" />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="media-upload-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          {uploading ? 'Uploading…' : 'Upload image or PDF'}
        </Button>
        <p className="text-xs text-gray-500 mt-1">Images (jpg/png/webp) or PDFs. Max ~10MB per file.</p>
      </div>
    </div>
  );
}
